import Autocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useMemo, useState } from 'react';
import { StoneXLabeledItem } from '..';
import ListBox from '../ListBox';

interface PropsType<TItem> {
  options: TItem[];
  loading?: () => boolean;
  getOptionLabel: (q: TItem) => string;
  getOptionKey?: ((option: TItem) => string | number);
  onChange?: (event: React.SyntheticEvent<Element, Event>, value: TItem | null) => void;
  onUpdate?: (value: TItem | null) => void;
  label?: string;
  helperText?: string;
  error?: string;
  value?: TItem | null;
  renderOption?: ((props: React.HTMLAttributes<HTMLLIElement>, option: TItem, state: AutocompleteRenderOptionState) => React.ReactNode) | undefined;
  dropdownAutoWidth?: boolean;
  listKey?: React.Key | null | undefined;
  groupBy?: ((option: TItem) => string) | undefined;
  variant?: undefined | 'standard' | 'filled';
  width?: 'small' | string | number;
  fullWidth?: boolean;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  getOptionDisabled?: ((option: TItem) => boolean) | undefined;
  disableClearable?: boolean;
  isOptionEqualToValue?: ((option: TItem, value: TItem) => boolean) | undefined;
  showRequired?: boolean;
  dropdownWidth?: number;
  sx?: object;
}

export function StoneXAutocomplete<TItem>(props: PropsType<TItem>) {
  const [open, setOpen] = useState(false);
  
  const width = useMemo(() => {
    if (props.width === 'small') {
      return '180px';
    }

    if (props.fullWidth) {
      return '100%';
    }

    return props.width ?? '250px';
  }, [props.width, props.fullWidth]);

  function onChange(event: React.SyntheticEvent<Element, Event>, value: TItem | null) {
    props.onChange && props.onChange(event, value);
    props.onUpdate && props.onUpdate(value);
  }

  return (
      <StoneXLabeledItem label={props.label} width={width} fullWidth={props.fullWidth} showRequired={props.showRequired} >
        <Autocomplete
          disablePortal
          options={props.options}
          sx={{...props.sx, width: width}}
          getOptionDisabled={props.getOptionDisabled}
          componentsProps={{
            paper: {
              sx: {
                width: props?.dropdownWidth
              }
            }
          }}
          disabled={props.disabled}
          // PopperComponent={(popperProps: any) => <Popper {...popperProps} placement="bottom-start" />}
          getOptionLabel={props.getOptionLabel}
          getOptionKey={props.getOptionKey}
          onChange={onChange}
          value={props.value}
          disableClearable={props.disableClearable}
          isOptionEqualToValue={props.isOptionEqualToValue}
          open={open}
          key={props.listKey}
          groupBy={props.groupBy}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          loading={props.loading && props.loading()}
          ListboxComponent={ListBox}
          fullWidth={props.fullWidth}
          renderOption={props.renderOption}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                sx={{ '& .MuiInputBase-root': {width: width}, '& .MuiFormHelperText-root': { marginLeft: 0 } }}
                name={props.name}
                onClick={() => setOpen(true)}
                error={!!props.error}
                helperText={props.error ?? props.helperText}
                variant={props.variant}
                placeholder={props.placeholder}
              />
            );
          }}
        />
      </StoneXLabeledItem>
  );
}
